import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { doc, getDoc, updateDoc, collection, getDocs, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, storage } from '../firebase';
import { useAppData } from '../context/AppDataContext';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { FileText } from "lucide-react";

// Import our new components
import OrderHeader from '../components/OrderHeader';
import DealershipCard from '../components/DealershipCard';
import ApprovalSection from '../components/ApprovalSection';
import OrderDetailsSection from '../components/OrderDetailsSection';
import VehiclesSection from '../components/VehiclesSection';
import PrizeboardSection from '../components/PrizeboardSection';
import PrizesSection from '../components/PrizesSection';
import MessageForm from '../components/MessageForm';
import MessageLog from '../components/MessageLog';

import '../styles/ClientList.css';
import '../styles/ViewOrderPage.css';
import '../styles/ViewOrderBento.css';

// Magic Bento layout primitives
import { BentoGrid, BentoCard, SectionModal } from '../components/OrderBento';

import { generateContractsFromTemplates } from '../utils/contracts';

import WinningNumbersGenerator from '../components/WinningNumbersGenerator';
import CopyOrderModal from '../components/CopyOrderModal';
import ImportFromOrderModal from '../components/ImportFromOrderModal';



// Import validation function
import { validateOrderData } from '../utils/validation';

// FTC compliance layer (enhanced-tier clients only)
import ComplianceSection from '../components/ComplianceSection';
import { buildFtcDisclaimer, runAutoChecks, getChecklistProgress } from '../utils/ftcCompliance';

function ViewOrderPage() {
  const { orderId } = useParams();
  const { refresh } = useAppData();
  const navigate = useNavigate();
  
  // State management
  const [orderDetails, setOrderDetails] = useState({
    jobNumber: '',
    campaignName: '', size: '', attachment: '', printer: '', quantity: '', sort: '', 
    sortOther: '', printerOther: '', notes: '',
    paperType: '',
    paperTypeOther: '',
    drops: [{ startDate: '', endDate: '', dduDate: '' }],
    staffed: false,
    sentToTravis: false,
  });

  const [vehicles, setVehicles] = useState([
  { 
    year: '', 
    make: '', 
    model: '', 
    salePrice: '', 
    apr: 5.9, 
    months: 84, 
    down: 20, 
    stockNumber: '',
    financeType: 'finance', 
    leaseMonths: 48, 
    totalDueAtSigning: '' 
  }
]);
  const [prizes, setPrizes] = useState(Array(5).fill().map(() => ({
    prize: '', custom: '', odds: '', value: ''
  })));
  
  const [totalOdds, setTotalOdds] = useState(50000);
  const [dealershipInfo, setDealershipInfo] = useState(null);
  const [clientId, setClientId] = useState(null);
  const [adminApproval, setAdminApproval] = useState("Pending");
  const [clientApproval, setClientApproval] = useState("Pending");
  const [artUploaded, setArtUploaded] = useState(false);
  const [insuranceLinked, setInsuranceLinked] = useState(false);
  const [prizeboardUploaded, setPrizeboardUploaded] = useState(false);
  const [proofs, setProofs] = useState([]);
  const [insuranceFile, setInsuranceFile] = useState(null);
  const [insurancePath, setInsurancePath] = useState(null);
  const [prizeboardFile, setPrizeboardFile] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [status, setStatus] = useState("");
  const [originalOrder, setOriginalOrder] = useState(null);
  const [isEditingDisclaimer, setIsEditingDisclaimer] = useState(false);
  const [clearClientLog, setClearClientLog] = useState(0);
  const [clearInternalLog, setClearInternalLog] = useState(0);
  const [manualDisclaimer, setManualDisclaimer] = useState('');
  const [disclaimerEdited, setDisclaimerEdited] = useState(false);

  const [generatedContracts, setGeneratedContracts] = useState([]);
  const [isGeneratingContract, setIsGeneratingContract] = useState(false);

  const [showCopyModal, setShowCopyModal] = useState(false);
  const [importModal, setImportModal] = useState({ open: false, mode: null }); // NEW
  const [analyticsData, setAnalyticsData] = useState([]);
  
  // NEW: Track manual prize odds changes to prevent auto-reset
  const [manualPrizeOdds, setManualPrizeOdds] = useState({});

  // FTC compliance checklist state: { [itemId]: { checked, by, at } }
  const [complianceChecklist, setComplianceChecklist] = useState({});

  // Bento layout: which section modal is open ('details' | 'vehicles' | 'prizes' | 'design' | 'disclaimer' | 'compliance' | 'insurance' | 'prizeboard' | null)
  const [activeModal, setActiveModal] = useState(null);

  // Fullscreen viewer for the latest design proof (opens without entering the modal)
  const [designFullscreen, setDesignFullscreen] = useState(false);

  useEffect(() => {
    if (!designFullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setDesignFullscreen(false); };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [designFullscreen]);

  // Refs
  const insuranceInputRef = useRef(null);
  

  // Helper functions
  const getStatusColor = () => {
    switch (status) {
      case 'Order Complete': return 'bg-green-500';
      case 'Pending Admin Approval':
      case 'Pending Client Approval':
      case 'Pending Prizeboard Approval': return 'bg-yellow-500';
      case 'On Hold':
      case 'Cancelled': return 'bg-gray-500';
      default: return 'bg-red-500';
    }
  };

  const getArtDueDate = () => {
    const drop1Start = orderDetails.drops?.[0]?.startDate;
    if (!drop1Start) return null;
    const date = new Date(drop1Start);
    if (isNaN(date)) return null;
    date.setDate(date.getDate() - 12);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long', 
      day: 'numeric'
    });
  };

  // Calculate average response rate for this order
  const getOrderResponseRate = () => {
    const assignedCampaigns = [];
    
    analyticsData.forEach(record => {
      if (record.assignments && record.campaigns) {
        record.campaigns.forEach(campaign => {
          if (record.assignments[campaign.title] === orderId) {
            const rate = parseFloat(campaign.responseRate?.replace('%', '')) || 0;
            if (rate > 0) {
              assignedCampaigns.push(rate);
            }
          }
        });
      }
    });
    
    if (assignedCampaigns.length === 0) return null;
    
    const average = assignedCampaigns.reduce((sum, rate) => sum + rate, 0) / assignedCampaigns.length;
    return average.toFixed(2);
  };

const generateInsuranceContract = async () => {
  setIsGeneratingContract(true);
  
  try {
    // Generate contracts using the template file
    const contracts = await generateContractsFromTemplates(
      orderDetails,
      dealershipInfo,
      prizes,
      orderId
    );
    
    // Save contracts to Firebase
    const savedContracts = await saveContractsToFirebase(contracts, orderId);
    setGeneratedContracts(savedContracts);
    
    alert('Insurance contracts generated successfully!');
  } catch (error) {
    console.error('Error generating contracts:', error);
    alert('Failed to generate contracts: ' + error.message);
  } finally {
    setIsGeneratingContract(false);
  }
};

// Keep this saveContractsToFirebase function:
const saveContractsToFirebase = async (contracts, orderId) => {
  const savedContracts = [];
  
  for (const contract of contracts) {
    const timestamp = Date.now();
    const filename = `${contract.name}_${timestamp}.pdf`;
    const fullPath = `insurance_contracts/${orderId}/${filename}`;
    
    try {
      const fileRef = ref(storage, fullPath);
      await uploadBytes(fileRef, contract.blob);
      const url = await getDownloadURL(fileRef);
      
      savedContracts.push({
        id: timestamp.toString(),
        name: contract.name,
        url: url,
        path: fullPath,
        createdAt: new Date(),
        type: 'insurance_contract'
      });
    } catch (error) {
      console.error(`Error uploading ${contract.name}:`, error);
    }
  }
  
  // Update the order document with contract information
  if (savedContracts.length > 0) {
    await updateDoc(doc(db, 'orders', orderId), {
      insuranceContracts: savedContracts,
      contractsGenerated: true,
      contractsGeneratedAt: serverTimestamp()
    });
  }
  
  return savedContracts;
};


  // Monthly payment calculation
  const calculateMonthlyPayment = (salePrice, downPct, apr, months) => {
    const downPayment = salePrice * (downPct / 100);
    const principal = salePrice - downPayment;
    const monthlyRate = apr / 100 / 12;
    const payment = monthlyRate * principal / (1 - Math.pow(1 + monthlyRate, -months));
    return isNaN(payment) ? 0 : Math.round(payment);
  };

  // FIXED: Disclaimer handling with notification
  const handleDisclaimerEdit = () => {
    if (!isEditingDisclaimer) {
      // Starting to edit - populate textarea with current disclaimer
      const currentDisclaimer = disclaimerEdited ? manualDisclaimer : generateDisclaimer();
      setManualDisclaimer(currentDisclaimer);
    }
    setIsEditingDisclaimer(!isEditingDisclaimer);
  };

  const handleDisclaimerSave = async () => {
    setDisclaimerEdited(true);
    setIsEditingDisclaimer(false);
    
    // FIXED: Send notification when disclaimer is manually edited
    await sendSystemMessage("Disclaimer Manually Edited", "Disclaimer was manually customized", orderId);
    
    // Save to Firebase
    if (orderId) {
      updateDoc(doc(db, 'orders', orderId), {
        customDisclaimer: manualDisclaimer,
        disclaimerEdited: true,
        updatedAt: serverTimestamp(),
      }).catch(err => console.error('Failed to save custom disclaimer:', err));
    }
  };

  const handleDisclaimerRegenerate = async () => {
    const regeneratedDisclaimer = generateDisclaimer();
    setManualDisclaimer(regeneratedDisclaimer);
    setDisclaimerEdited(false);
    
    // FIXED: Send notification when disclaimer is regenerated
    await sendSystemMessage("Disclaimer Regenerated", "Disclaimer was regenerated from order details", orderId);
    
    // Update Firebase
    if (orderId) {
      updateDoc(doc(db, 'orders', orderId), {
        customDisclaimer: null,
        disclaimerEdited: false,
        updatedAt: serverTimestamp(),
      }).catch(err => console.error('Failed to update disclaimer status:', err));
    }
  };

  const getDisplayDisclaimer = () => {
    if (disclaimerEdited) {
      return manualDisclaimer;
    }
    return generateDisclaimer();
  };

  const generateDisclaimer = () => {
    if (!vehicles || vehicles.length === 0 || !prizes || prizes.length === 0) return '';

    // FTC-enhanced clients (e.g. Morgan Auto Group) get the full compliance format
    if (dealershipInfo?.complianceTier === 'ftc-enhanced') {
      return buildFtcDisclaimer({ vehicles, prizes, orderDetails, dealershipInfo });
    }

    const vehicleLines = vehicles
      .filter(v => {
        // For lease: need make, model, salePrice (monthly payment), and leaseMonths
        if (v.financeType === 'lease') {
          return v.make && v.model && v.salePrice && v.leaseMonths;
        }
        // For finance: need make, model, salePrice, and months (original filter)
        return v.make && v.model && v.salePrice && v.months;
      })
      .map((v, i) => {
        if (v.financeType === 'lease') {
          // Lease format: 2025 Ford Bronco Sport Big Bend for $432 per month for 48 months // $3,731 total due at lease signing
          const make = v.make === 'Other' ? (v.customMake || v.make) : v.make;
          return `(${i + 1}) ${v.year} ${make} ${v.model} for $${v.salePrice} per month for ${v.leaseMonths || 48} months // $${v.totalDueAtSigning || '0'} total due at lease signing`;
        } else {
          // Finance format: (existing format)
          const make = v.make === 'Other' ? (v.customMake || v.make) : v.make;
          const monthlyPayment = calculateMonthlyPayment(v.salePrice, v.down, v.apr, v.months);
          return `(${i + 1}) ${v.year} ${make} ${v.model} for $${monthlyPayment} per month (Stock#${v.stockNumber} // Sale Price: $${v.salePrice} // ${v.apr}% APR for ${v.months} months // ${v.down}% down)`;
        }
      });

    const getFormattedDate = (rawDate) => {
      if (!rawDate) return '';
      // rawDate is "YYYY-MM-DD"
      const [year, month, day] = rawDate.split('-').map(Number);
      // monthIndex is zero-based (0=January)
      const date = new Date(year, month - 1, day);
      if (isNaN(date)) return '';
      return date.toLocaleDateString(undefined, {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
    };

    const validDrops = orderDetails?.drops?.filter(d => d.startDate && d.endDate) || [];
    const dropStart = getFormattedDate(validDrops[0]?.startDate) || 'Start Date';
    const dropEnd = getFormattedDate(validDrops.at(-1)?.endDate) || 'End Date';

    // FIXED: Use custom prize name when "Other" is selected
    const prizeLines = prizes
      .filter(p => p.prize)
      .map((p, i) => {
        // Use custom prize name if "Other" is selected, otherwise use the regular prize name
        const prizeName = p.prize === 'Other' ? (p.custom || 'Custom Prize') : p.prize;
        const part = `#${i + 1} ${prizeName}`;
        const val = p.value ? ` (Valued at ${p.value})` : '';
        const odds = p.odds ? ` ${p.odds}` : '';
        return part + val + odds;
      });

    // Check if we have any lease vehicles to include lease-specific disclaimer
    const hasLeaseVehicles = vehicles.some(v => v.financeType === 'lease');
    const hasFinanceVehicles = vehicles.some(v => v.financeType !== 'lease');

    let vehicleDisclaimer = '';
    
    if (hasFinanceVehicles) {
      vehicleDisclaimer += 'All vehicle offers are with approved credit subject to lender\'s approval. No payments for 90 days with approved credit-subject to lender\'s approval. Interest accrues from date of purchase. Restrictions may apply. See dealer for complete details. APR and discounts are subject to change. Cannot combine any offers.';
    }

    if (hasLeaseVehicles) {
      if (hasFinanceVehicles) vehicleDisclaimer += ' '; // Single space instead of \n\n
      vehicleDisclaimer += 'Lease offers are with approved credit through manufacturer\'s financial services. All lease offers include 10,000 miles per year. Excess mileage charges may apply. See dealer for complete lease terms and conditions.';
    }

    // Changed all \n\n to single spaces or single \n where line breaks are actually needed
    return `${vehicleLines.join(' ')} ${vehicleDisclaimer} *NO PURCHASE NECESSARY. Match your winning number to the Prize Board at the dealership to determine what prize you have won. Odds of winning ${prizeLines.join(' ')}. All taxes are the responsibility of the prize winner(s). Contest begins ${dropStart} and ends ${dropEnd}. Everyone must register to receive their prize. Purchase does not increase chance of winning. Contest open to legal US residents age 18 or older with a valid driver's license who received an original mail piece via US mail. Participants agree to all contest rules. See dealer for complete contest rules. ${dealershipInfo?.name || '<Client Name>'} and associates, mail house, associated sponsors or agencies, and their family members and members of same household are ineligible. Addressee must redeem original mail piece in person by close of business ${dropEnd}. Any unclaimed prizes will not be awarded. All photos are for illustration purposes only. Vehicles are subject to prior sale. Void where prohibited by law. All offers end ${dropEnd}.`;
  };

  // Helper function to get dynamic greeting based on EST time
  const getGreeting = () => {
    // Get current time in EST
    const now = new Date();
    const estTime = new Date(now.toLocaleString('en-US', { timeZone: 'America/New_York' }));
    const hour = estTime.getHours();
    
    if (hour < 12) {
      return 'Good morning';
    } else if (hour < 17) {
      return 'Good afternoon';
    } else {
      return 'Good evening';
    }
  };

  // Helper function to convert YYYY-MM-DD to MM/DD/YYYY
  const formatDateToMMDDYYYY = (dateString) => {
    if (!dateString) return '';
    
    // If the date is already in MM/DD/YYYY format, return as is
    if (dateString.match(/^\d{1,2}\/\d{1,2}\/\d{4}$/)) {
      return dateString;
    }
    
    // Convert YYYY-MM-DD to MM/DD/YYYY
    if (dateString.match(/^\d{4}-\d{2}-\d{2}$/)) {
      const [year, month, day] = dateString.split('-');
      return `${parseInt(month)}/${parseInt(day)}/${year}`;
    }
    
    // If it's some other format, try to parse and format
    const date = new Date(dateString);
    if (!isNaN(date.getTime())) {
      return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`;
    }
    
    return dateString; // Return original if we can't parse it
  };

  // Bitly API function to shorten links
  const createBitlyLink = async (longUrl) => {
    try {
      const response = await fetch(
        'https://us-central1-srmg-order-system.cloudfunctions.net/createBitlyLink',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ longUrl })
        }
      );
      
      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error || 'Failed to create Bitly link');
      }
      
      const data = await response.json();
      return data.link;
    } catch (error) {
      console.error('Error creating Bitly link:', error);
      throw error;
    }
  };

  const handleSendToClient = async () => {
    try {
      // Validate that we have client email
      if (!dealershipInfo?.email) {
        alert('No client email found. Please add an email to the client profile.');
        return;
      }

      // Get the most recent proof
      if (!proofs || proofs.length === 0) {
        alert('No designs have been uploaded yet. Please upload a design before sending to client.');
        return;
      }

      // Sort proofs by upload date (most recent first)
      const sortedProofs = [...proofs].sort((a, b) => {
        const dateA = a.uploadedAt?.toDate?.() || new Date(a.uploadedAt || 0);
        const dateB = b.uploadedAt?.toDate?.() || new Date(b.uploadedAt || 0);
        return dateB - dateA;
      });

      const mostRecentProof = sortedProofs[0];

      // Validate that the proof has a URL
      if (!mostRecentProof.fileUrl) {
        alert('Design URL not found. Please try re-uploading the design.');
        console.error('Proof object missing fileUrl:', mostRecentProof);
        return;
      }

      console.log('Creating shortened link for:', mostRecentProof.fileUrl);

      // Create bitly link for the design
      let designLink = mostRecentProof.fileUrl;
      try {
        const fullBitlyLink = await createBitlyLink(mostRecentProof.fileUrl);
        // Keep the full link with https:// for email clickability
        designLink = fullBitlyLink;
        console.log('Bitly link created:', designLink);
      } catch (error) {
        console.error('Error creating bitly link, using original URL:', error);
        // If bitly fails, use the original URL
      }

      // Copy link to clipboard
      try {
        await navigator.clipboard.writeText(designLink);
        alert('✓ Link copied to clipboard! Paste it into the email when ready.');
      } catch (clipboardError) {
        console.error('Failed to copy to clipboard:', clipboardError);
        alert('Could not copy link automatically. Link: ' + designLink);
      }

      const recipients = [dealershipInfo.email];
      const cc = ['will@srmgauto.com', 'ty@srmgauto.com'];
      
      const subject = `Your Artwork is Ready for Review - Job #${orderDetails.jobNumber}`;

      const body = `${getGreeting()},

Your custom artwork for ${orderDetails.campaignName || 'your campaign'} is ready for your review!

Please click the link below to view your design:
[VIEW DESIGN HERE]

If you have any feedback or would like any changes, please let us know. Once you approve, we'll proceed with finalizing your order.

Job Number: ${orderDetails.jobNumber}
${orderDetails.campaignName ? `Campaign: ${orderDetails.campaignName}` : ''}

Thank you for your business!

Best regards,
SRMG Team`;

      // Build mailto link with CC
      const mailtoLink = `mailto:${recipients.join(',')}` +
        `?cc=${cc.join(',')}` +
        `&subject=${encodeURIComponent(subject)}` +
        `&body=${encodeURIComponent(body)}`;

      // Open default mail client
      window.location.href = mailtoLink;

    } catch (error) {
      console.error('Error in handleSendToClient:', error);
      alert('Error preparing email. Please try again.');
    }
  };

  const handleSendToJSDirect = () => {

    const recipients = [
      'orders@gojsdirect.com',
      'joev@srmgauto.com',
      'will@srmgauto.com',
      'ty@srmgauto.com',
      'joshs@srmgauto.com',
      'courtney@srmgauto.com',
      'drichards@gojsdirect.com',
    ];

    const subject = `**NEW ORDER** ${dealershipInfo?.name || ''} Job #${orderDetails.jobNumber}`;

    const addressLines = `${dealershipInfo?.street1 || ''}\n${dealershipInfo?.city || ''}, ${dealershipInfo?.state || ''} ${dealershipInfo?.zip || ''}`;

    const totalQty = orderDetails.quantity || '';
    const size = orderDetails.size || '';
    const attachment = orderDetails.attachment || '';
    const sort = orderDetails.sort || '';

    const multipleDrops = orderDetails.drops.length > 1;
    const artworkLine = `One Artwork${multipleDrops ? ' - Variable Dates' : ''}`;

    let saleDates = '';
    let dduDates = '';

    orderDetails.drops.forEach((drop, idx) => {
      const dropNum = `Drop#${idx + 1}`;
      // Format the dates to MM/DD/YYYY
      const startDate = formatDateToMMDDYYYY(drop.startDate);
      const endDate = formatDateToMMDDYYYY(drop.endDate);
      const dduDate = formatDateToMMDDYYYY(drop.dduDate);
      
      saleDates += `${dropNum} ${startDate} - ${endDate}\n`;
      dduDates += `${dropNum} ${dduDate}\n`;
    });

const paperType = orderDetails.paperType === 'Other' 
  ? (orderDetails.paperTypeOther || 'Custom Paper Type')
  : (orderDetails.paperType || '#100 Gloss Cover/9 pt Silk Cover/Offset/Laminated/Gloss Text');

    const body = `${getGreeting()},

Below are the job details for ${dealershipInfo?.name || ''} Job #${orderDetails.jobNumber}:

Address:
${addressLines}

Total Qty: ${totalQty} - ${artworkLine}

Product: ${size} ${paperType} with ${attachment}

Sort: ${sort}

Sale Dates:
${saleDates}

DDU Delivery Date:
${dduDates}
    `;

    // Build mailto link
    const mailtoLink = `mailto:${recipients.join(',')}` +
      `?subject=${encodeURIComponent(subject)}` +
      `&body=${encodeURIComponent(body)}`;

    // Open default mail client
    window.location.href = mailtoLink;
  };

  // FIXED: Send for approval function - REMOVED PWA CALLS
  const handleSendForApproval = async (approvalType) => {
    // Compliance orders (medium + ftc-enhanced): block accidental sends with an incomplete checklist
    const tier = dealershipInfo?.complianceTier;
    if (tier === 'ftc-enhanced' || tier === 'medium') {
      const autoResults = runAutoChecks(vehicles, prizes, orderDetails, dealershipInfo, tier);
      const progress = getChecklistProgress(complianceChecklist, autoResults, tier);
      if (!progress.complete) {
        const preview = progress.outstanding.slice(0, 8).map(t => `• ${t}`).join('\n');
        const more = progress.outstanding.length > 8 ? `\n…and ${progress.outstanding.length - 8} more` : '';
        const proceed = window.confirm(
          `⚠️ COMPLIANCE WARNING (${tier === 'ftc-enhanced' ? 'FTC-Enhanced' : 'Medium'})\n\n${progress.outstanding.length} checklist item(s) are incomplete:\n\n${preview}${more}\n\nSend for ${approvalType} approval anyway?`
        );
        if (!proceed) return;
      }
    }

    try {
      const updates = {};
      
      if (approvalType === 'admin') {
        updates.status = 'Pending Admin Approval';
        updates.adminApproval = 'Pending';
      } else if (approvalType === 'client') {
        updates.status = 'Pending Client Approval';
        updates.clientApproval = 'Pending';
        
        // Send notification to client
        await sendSystemMessage("Design Ready", "Your design is ready for review", orderId);
      }

      await updateDoc(doc(db, 'orders', orderId), updates);
      setStatus(updates.status);
      
      if (approvalType === 'admin') {
        setAdminApproval('Pending');
      } else {
        setClientApproval('Pending');
      }

      alert(`Sent for ${approvalType} approval successfully!`);
    } catch (error) {
      console.error('Error sending for approval:', error);
      alert('Failed to send for approval');
    }
  };

  // FIXED: Clean version of handleSave with detailed change tracking - REMOVED PWA CALLS
  const handleSave = async () => {
    try {
      const validationErrors = validateOrderData(orderDetails, vehicles, prizes);
      
      if (validationErrors.length > 0) {
        alert('Please fix the following errors:\n\n' + validationErrors.join('\n'));
        return;
      }

      // Track detailed changes for system messages
      if (originalOrder) {
        const detailedChanges = [];
        
        // Check order details changes
        const orderDetailChanges = detectDetailedChanges(originalOrder, orderDetails);
        detailedChanges.push(...orderDetailChanges);
        
        // Check vehicle changes
        const vehicleChanges = detectVehicleChanges(originalOrder.vehicles, vehicles);
        detailedChanges.push(...vehicleChanges);
        
        // Check prize changes
        const prizeChanges = detectPrizeChanges(originalOrder.prizes, prizes);
        detailedChanges.push(...prizeChanges);
        
        // Send detailed system message if there are changes
        if (detailedChanges.length > 0) {
          const changeMessage = detailedChanges.join('; ');
          await sendSystemMessage("Order Updated", changeMessage, orderId);
        }
      }

      await updateDoc(doc(db, 'orders', orderId), {
        ...orderDetails,
        vehicles,
        prizes,
        totalOdds,
        insuranceLinked,
        prizeboardUploaded,
        artUploaded,
        adminApproval,
        clientApproval,
        status,
        insuranceFile,
        insurancePath, 
        prizeboardFile,
        customDisclaimer: disclaimerEdited ? manualDisclaimer : null,
        disclaimerEdited,
        complianceChecklist,
        updatedAt: serverTimestamp(),
      });
      
      // Update the original order reference for future comparisons
      setOriginalOrder({
        orderDetails: { ...orderDetails },
        vehicles: [...vehicles],
        prizes: [...prizes]
      });
      
      alert('Order updated successfully!');
      refresh(['orders']);
      setIsEditing(false);
    } catch (err) {
      console.error('Save error:', err);
      alert('Failed to update order: ' + err.message);
    }
  };

  const sendSystemMessage = async (type, value, orderIdToSend) => {
    if (!clientId || !orderIdToSend) return;

    let dealershipName = '';
    try {
      const clientSnap = await getDoc(doc(db, 'clients', clientId));
      if (clientSnap.exists()) {
        dealershipName = clientSnap.data().name || '';
      }
    } catch (err) {
      console.warn("Could not fetch dealership name:", err);
    }

    const jobNumber = orderDetails.jobNumber || 'Unknown';
    
    let messageText;
    if (type === "Order Updated") {
      messageText = `Job #${jobNumber}: ${value}`;
    } else {
      messageText = `Job #${jobNumber}: ${type} updated to: ${value}`;
    }

    const message = {
      sender: "system",
      senderName: "System",
      senderRole: "system",
      recipientType: "internal",
      text: messageText,
      createdAt: serverTimestamp(),
      read: false,
      clientId,
      orderId: orderIdToSend,           // Add order context
      orderNumber: jobNumber,           // Add order number
      campaignName: orderDetails.campaignName || '',  // Add campaign name
      isOrderMessage: true,             // Flag as order message
      dealershipName
    };

    try {
      await addDoc(collection(db, "clients", clientId, "messages"), message);
    } catch (error) {
      console.error('Error sending system message:', error);
    }
  };

  const handleDownload = async (url, filename) => {
  try {
    const response = await fetch(url);
    const blob = await response.blob();
    const blobUrl = URL.createObjectURL(blob);
    
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
  } catch (error) {
    console.error('Download failed:', error);
    // Fallback to direct link
    window.open(url, '_blank');
  }
};

  // Clean version of detectDetailedChanges
  const detectDetailedChanges = (original, current) => {
    const changes = [];
    
    // Helper function to format field names
    const formatFieldName = (field) => {
      const fieldNames = {
        jobNumber: 'Job Number',
        campaignName: 'Campaign Name',
        campaignNameOther: 'Custom Campaign Name',
        size: 'Size',
        attachment: 'Attachment',
        printer: 'Printer',
        printerOther: 'Custom Printer',
        quantity: 'Quantity',
        sort: 'Sort',
        sortOther: 'Custom Sort',
        notes: 'Notes',
        staffed: 'Staffed Status',
        sentToTravis: 'Sent to Travis Status',
        paperType: 'Paper Type'
      };
      return fieldNames[field] || field;
    };

    // Check order details changes
    if (original?.orderDetails && current) {
      const orderFields = ['jobNumber', 'campaignName', 'campaignNameOther', 'size', 'attachment', 'printer', 'printerOther', 'quantity', 'sort', 'sortOther', 'notes', 'staffed', 'sentToTravis', 'paperType'];
      
      orderFields.forEach(field => {
        const oldValue = original.orderDetails[field];
        const newValue = current[field];
        
        if (oldValue !== newValue) {
          const fieldName = formatFieldName(field);
          
          if (field === 'staffed' || field === 'sentToTravis') {
            changes.push(`${fieldName} changed to ${newValue ? 'Yes' : 'No'}`);
          } else if (oldValue && newValue) {
            changes.push(`${fieldName} changed from "${oldValue}" to "${newValue}"`);
          } else if (newValue) {
            changes.push(`${fieldName} set to "${newValue}"`);
          } else if (oldValue) {
            changes.push(`${fieldName} cleared`);
          }
        }
      });

      // Check drops changes
      const oldDrops = original.orderDetails.drops || [];
      const newDrops = current.drops || [];
      
      if (JSON.stringify(oldDrops) !== JSON.stringify(newDrops)) {
        if (newDrops.length > oldDrops.length) {
          changes.push(`Added ${newDrops.length - oldDrops.length} drop(s)`);
        } else if (newDrops.length < oldDrops.length) {
          changes.push(`Removed ${oldDrops.length - newDrops.length} drop(s)`);
        } else {
          // Same number of drops, check for modifications
          newDrops.forEach((drop, index) => {
            const oldDrop = oldDrops[index];
            if (oldDrop) {
              if (drop.startDate !== oldDrop.startDate) {
                changes.push(`Drop ${index + 1} start date changed to ${drop.startDate}`);
              }
              if (drop.endDate !== oldDrop.endDate) {
                changes.push(`Drop ${index + 1} end date changed to ${drop.endDate}`);
              }
              if (drop.dduDate !== oldDrop.dduDate) {
                changes.push(`Drop ${index + 1} DDU date changed to ${drop.dduDate}`);
              }
              if (drop.quantity !== oldDrop.quantity) {
                changes.push(`Drop ${index + 1} quantity changed to ${drop.quantity}`);
              }
            }
          });
        }
      }
    }

    return changes;
  };

  // Updated detectVehicleChanges function for ViewOrderPage.js
  const detectVehicleChanges = (originalVehicles, currentVehicles) => {
    const changes = [];
    const oldVehicles = originalVehicles || [];
    const newVehicles = currentVehicles || [];

    if (newVehicles.length > oldVehicles.length) {
      changes.push(`Added ${newVehicles.length - oldVehicles.length} vehicle(s)`);
    } else if (newVehicles.length < oldVehicles.length) {
      changes.push(`Removed ${oldVehicles.length - newVehicles.length} vehicle(s)`);
    }

    // Check for modifications in existing vehicles
    newVehicles.forEach((vehicle, index) => {
      const oldVehicle = oldVehicles[index];
      if (oldVehicle) {
        // Updated fields list to include new lease fields
        const vehicleFields = [
          'year', 'make', 'model', 'salePrice', 'apr', 'months', 'down', 
          'stockNumber', 'financeType', 'leaseMonths', 'totalDueAtSigning'
        ];
        
        vehicleFields.forEach(field => {
          if (vehicle[field] !== oldVehicle[field]) {
            const vehicleLabel = `Vehicle ${index + 1}`;
            let fieldName;
            
            switch(field) {
              case 'salePrice':
                fieldName = vehicle.financeType === 'lease' ? 'Monthly Payment' : 'Sale Price';
                break;
              case 'apr':
                fieldName = 'APR';
                break;
              case 'stockNumber':
                fieldName = 'Stock Number';
                break;
              case 'financeType':
                fieldName = 'Finance Type';
                break;
              case 'leaseMonths':
                fieldName = 'Lease Term';
                break;
              case 'totalDueAtSigning':
                fieldName = 'Total Due at Signing';
                break;
              default:
                fieldName = field.charAt(0).toUpperCase() + field.slice(1);
            }
            
            if (oldVehicle[field] && vehicle[field]) {
              // Handle special formatting for certain fields
              let oldValue = oldVehicle[field];
              let newValue = vehicle[field];
              
              if (field === 'leaseMonths') {
                oldValue = `${oldValue} months`;
                newValue = `${newValue} months`;
              } else if (field === 'totalDueAtSigning' || (field === 'salePrice' && vehicle.financeType === 'lease')) {
                oldValue = `$${oldValue}`;
                newValue = `$${newValue}`;
              } else if (field === 'apr') {
                oldValue = `${oldValue}%`;
                newValue = `${newValue}%`;
              } else if (field === 'down') {
                oldValue = `${oldValue}%`;
                newValue = `${newValue}%`;
              }
              
              changes.push(`${vehicleLabel} ${fieldName} changed from "${oldValue}" to "${newValue}"`);
            } else if (vehicle[field]) {
              let newValue = vehicle[field];
              if (field === 'leaseMonths') {
                newValue = `${newValue} months`;
              } else if (field === 'totalDueAtSigning' || (field === 'salePrice' && vehicle.financeType === 'lease')) {
                newValue = `$${newValue}`;
              } else if (field === 'apr') {
                newValue = `${newValue}%`;
              } else if (field === 'down') {
                newValue = `${newValue}%`;
              }
              
              changes.push(`${vehicleLabel} ${fieldName} set to "${newValue}"`);
            } else if (oldVehicle[field]) {
              changes.push(`${vehicleLabel} ${fieldName} cleared`);
            }
          }
        });
      }
    });

    return changes;
  };

  // Clean version of detectPrizeChanges
  const detectPrizeChanges = (originalPrizes, currentPrizes) => {
    const changes = [];
    const oldPrizes = originalPrizes || [];
    const newPrizes = currentPrizes || [];

    // Check each prize slot
    newPrizes.forEach((prize, index) => {
      const oldPrize = oldPrizes[index];
      if (oldPrize) {
        const prizeFields = ['prize', 'custom', 'odds', 'value'];
        
        prizeFields.forEach(field => {
          if (prize[field] !== oldPrize[field]) {
            const prizeLabel = `Prize ${index + 1}`;
            const fieldName = field === 'prize' ? 'Type' : 
                             field === 'custom' ? 'Custom Description' : 
                             field === 'odds' ? 'Odds' : 
                             field === 'value' ? 'Value' : 
                             field.charAt(0).toUpperCase() + field.slice(1);
            
            if (oldPrize[field] && prize[field]) {
              changes.push(`${prizeLabel} ${fieldName} changed from "${oldPrize[field]}" to "${prize[field]}"`);
            } else if (prize[field]) {
              changes.push(`${prizeLabel} ${fieldName} set to "${prize[field]}"`);
            } else if (oldPrize[field]) {
              changes.push(`${prizeLabel} ${fieldName} cleared`);
            }
          }
        });
      }
    });

    return changes;
  };

  const handleFileUpload = async (file, type) => {
    if (!file || !orderId) return;
    const orderRef = doc(db, 'orders', orderId);

    let folder = 'proofs';
    if (type === 'insurance') folder = 'insurance';
    if (type === 'prizeboard') folder = 'prizeboard';

    const timestamp = Date.now();
    const filename = `${timestamp}_${file.name}`;
    const fullPath = `${folder}/${orderId}/${filename}`;
    const fileRef = ref(storage, fullPath);

    try {
      await uploadBytes(fileRef, file);
      const url = await getDownloadURL(fileRef);

      const updates = {};
      if (type === 'insurance') {
        updates.insuranceFile = url;
        updates.insurancePath = fullPath;
        updates.insuranceLinked = true;
        setInsuranceFile(url);
        setInsurancePath(fullPath);
        setInsuranceLinked(true);
      } else if (type === 'prizeboard') {
        updates.prizeboardFile = url;
        updates.prizeboardUploaded = true;
        setPrizeboardFile(url);
        setPrizeboardUploaded(true);
      } else {
        // Determine approval stage based on current status
        let approvalStage = 'admin'; // Default to admin review
        if (adminApproval === 'Approved' && clientApproval !== 'Approved') {
          approvalStage = 'client'; // If admin approved but client hasn't, this is for client review
        }

        const proofCol = collection(db, 'orders', orderId, 'proofs');
        await addDoc(proofCol, {
          fileUrl: url,
          status: 'pending',
          approvalStage: approvalStage, // Add this field
          clientComment: '',
          adminComment: '',
          uploadedAt: serverTimestamp(),
        });
        const qSnap = await getDocs(proofCol);
        const latest = qSnap.docs.map(d => ({ id: d.id, ...d.data() }));
        setProofs(latest);
        updates.artUploaded = true;
        setArtUploaded(true);
      }

      await updateDoc(orderRef, updates);
      alert(`${type.charAt(0).toUpperCase() + type.slice(1)} file uploaded successfully!`);
    } catch (err) {
      console.error('Upload error:', err);
      alert(`Failed to upload ${type} file: ${err.message}`);
    }
  };

  // FIXED: REMOVED PWA CALLS from approval change handler
  const handleApprovalChange = async (value, type) => {
    if (type === 'admin') {
      if (value !== adminApproval) sendSystemMessage("Admin Approval", value, orderId);
      setAdminApproval(value);
    }
    if (type === 'client') {
      if (value !== clientApproval) sendSystemMessage("Client Approval", value, orderId);
      setClientApproval(value);
    }
  };

  // FIXED: REMOVED PWA CALLS from status change handler
  const handleStatusChange = async (newStatus) => {
    sendSystemMessage("Order Status", newStatus, orderId);
    setStatus(newStatus);
    setOrderDetails(prev => ({ ...prev, status: newStatus }));
  };

  // FIXED: Custom prizes section with manual odds tracking
  const handlePrizeChange = (index, field, value) => {
    const updated = [...prizes];
    updated[index][field] = value;

    // FIXED: Track manual odds changes to prevent auto-reset
    if (field === 'odds') {
      setManualPrizeOdds(prev => ({
        ...prev,
        [index]: true  // Mark this prize's odds as manually set
      }));
    }

    // Only auto-generate odds if not manually set and prize is being selected
    if (field === 'prize' && !manualPrizeOdds[index]) {
      switch (index) {
        case 0:
        case 1:
        case 2:
          updated[index].odds = `1:${totalOdds.toLocaleString()}`;
          break;
        case 3:
          updated[index].odds = `${(totalOdds - 4).toLocaleString()}:${totalOdds.toLocaleString()}`;
          break;
        case 4:
          updated[index].odds = `1:${totalOdds.toLocaleString()}`;
          break;
        default:
          break;
      }
    }

    setPrizes(updated);
  };

 // FIXED: Only auto-update odds for prizes that haven't been manually set
  const handleTotalOddsChange = (newTotalOdds) => {
    setTotalOdds(newTotalOdds);
    
    // Only update odds for prizes that haven't been manually edited
    const updatedPrizes = prizes.map((prize, index) => {
      if (prize.prize && !manualPrizeOdds[index]) {
        let newOdds = '';
        switch (index) {
          case 0:
          case 1:
          case 2:
            newOdds = `1:${newTotalOdds.toLocaleString()}`;
            break;
          case 3:
            newOdds = `${(newTotalOdds - 4).toLocaleString()}:${newTotalOdds.toLocaleString()}`;
            break;
          case 4:
            newOdds = `1:${newTotalOdds.toLocaleString()}`;
            break;
          default:
            newOdds = prize.odds; // Keep existing odds
            break;
        }
        return { ...prize, odds: newOdds };
      }
      return prize; // Return unchanged if manually edited or no prize selected
    });
    
    setPrizes(updatedPrizes);
  };

  // Load data on component mount
  useEffect(() => {
    const fetchOrder = async () => {
      const docRef = doc(db, 'orders', orderId);
      const docSnap = await getDoc(docRef);
      if (!docSnap.exists()) return;

      const data = docSnap.data();
      setOriginalOrder({ ...data, vehicles: data.vehicles || [], prizes: data.prizes || [] });
      
      setOrderDetails(prev => ({
        ...prev,
        ...data,
        drops: data.drops?.length > 0 ? data.drops : [{ startDate: '', endDate: '', dduDate: '', quantity: '' }],
        sentToTravis: data.sentToTravis || false,
      }));

      if (data.prizes?.length > 0) {
        setPrizes(data.prizes);
      }

      if (data.customDisclaimer) {
        setManualDisclaimer(data.customDisclaimer);
        setDisclaimerEdited(data.disclaimerEdited || false);
      }

      setComplianceChecklist(data.complianceChecklist || {});
      
      setVehicles(data.vehicles || [{ 
        year: '', 
        make: '', 
        model: '', 
        salePrice: '', 
        apr: 5.9, 
        months: 84, 
        down: 20, 
        stockNumber: '',
        financeType: 'finance',
        leaseMonths: 48,
        totalDueAtSigning: ''
      }]);
      setStatus(data.status || '');
      setAdminApproval(data.adminApproval || 'Pending');
      setClientApproval(data.clientApproval || 'Pending');
      setArtUploaded(data.artUploaded || false);
      setInsuranceFile(data.insuranceFile || null);
      setInsurancePath(data.insurancePath || null);
      setInsuranceLinked(data.insuranceLinked || false);
      setPrizeboardFile(data.prizeboardFile || null);
      setClientId(data.clientId || null);
      
      // Fetch analytics data
      try {
        const analyticsSnap = await getDocs(collection(db, 'analytics'));
        setAnalyticsData(analyticsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      } catch (error) {
        console.error('Error fetching analytics:', error);
      }

      // FIXED: More accurate manual odds detection
      const manualOddsMap = {};
      const currentTotalOdds = data.totalOdds || 50000;
      
      if (data.prizes) {
        data.prizes.forEach((prize, index) => {
          if (prize.odds && prize.prize) {
            // Generate what the auto odds SHOULD be for this prize at this position
            const expectedAutoOdds = getAutoGeneratedOdds(index, currentTotalOdds);
            
            // Only mark as manual if the odds don't match what would be auto-generated
            // AND the odds aren't empty (which would indicate no auto-generation happened)
            if (prize.odds !== expectedAutoOdds && prize.odds !== '') {
              manualOddsMap[index] = true;
            }
          }
        });
      }
      
      setManualPrizeOdds(manualOddsMap);
      
      // IMPORTANT: Set the totalOdds state from the loaded data
      if (data.totalOdds) {
        setTotalOdds(data.totalOdds);
      }
    };

    fetchOrder();
  }, [orderId]);

  // Helper function to determine what auto-generated odds should be
  const getAutoGeneratedOdds = (index, totalOdds) => {
    switch (index) {
      case 0:
      case 1:
      case 2:
        return `1:${totalOdds.toLocaleString()}`;
      case 3:
        return `${(totalOdds - 18).toLocaleString()}:${totalOdds.toLocaleString()}`;
      case 4:
        return `15:${totalOdds.toLocaleString()}`;
      default:
        return '';
    }
  };

  const handleCopyOrder = () => {
  setShowCopyModal(true);
};

  // NEW: Open import modal for prizes or vehicles
  const handleOpenImportModal = (mode) => {
    setImportModal({ open: true, mode });
  };

  // NEW: Handle the actual import once user confirms in modal
  const handleImportFromOrder = (type, data) => {
    if (type === 'prizes') {
      setPrizes(data.prizes && data.prizes.length > 0
        ? data.prizes
        : Array(5).fill(null).map(() => ({ prize: '', custom: '', odds: '', value: '' }))
      );
      if (data.totalOdds) setTotalOdds(data.totalOdds);
    } else if (type === 'vehicles') {
      setVehicles(prev => [...prev, ...data.vehicles]);
    }
  };

// Add this new function to handle the actual copying logic
const handleCopyOrderSubmit = (copiedData) => {
  navigate('/orders/new', {
    state: { 
      copiedOrder: copiedData,
      // Don't pass vehicles and prizes separately since they're now included in copiedData if selected
      vehicles: copiedData.vehicles || [],
      prizes: copiedData.prizes || []
    }
  });
};

  

  // Fetch client data
  useEffect(() => {
    const fetchClient = async () => {
      if (!clientId) return;
      const docSnap = await getDoc(doc(db, 'clients', clientId));
      if (!docSnap.exists()) return;

      const c = docSnap.data();
      setDealershipInfo({
        name: c.name || '',
        blackLogo: c.blackLogo || '',
        phone: c.phone || '',
        generalManager: c.generalManager || '',
        street1: c.street1 || '',
        street2: c.street2 || '',
        city: c.city || '',
        zip: c.zip || '',
        state: c.state || '',
        email: c.email || '',
        dealershipWebsite: c.dealershipWebsite || '',
        complianceTier: c.complianceTier || 'standard',
        autoGroup: c.autoGroup || '',
        complianceFees: {
          preDeliveryFee: c.preDeliveryFee || '',
          privateTagFee: c.privateTagFee || '',
          electronicFilingFee: c.electronicFilingFee || '',
          docFee: c.docFee || '',
        },
      });
    };
    fetchClient();
  }, [clientId]);

  // Fetch proofs
  useEffect(() => {
    const fetchProofs = async () => {
      const proofRef = collection(db, 'orders', orderId, 'proofs');
      const querySnapshot = await getDocs(proofRef);
      const proofsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProofs(proofsList);
    };
    fetchProofs();
  }, [orderId]);

  // ===== Bento card summary helpers =====

  // Most recent proof (same sort logic as handleSendToClient)
  const getLatestProof = () => {
    if (!proofs || proofs.length === 0) return null;
    const sorted = [...proofs].sort((a, b) => {
      const dateA = a.uploadedAt?.toDate?.() || new Date(a.uploadedAt || 0);
      const dateB = b.uploadedAt?.toDate?.() || new Date(b.uploadedAt || 0);
      return dateB - dateA;
    });
    return sorted[0];
  };

  const isPdfUrl = (url) => {
    if (!url) return false;
    try {
      return /\.pdf(\?|$)/i.test(decodeURIComponent(url.split('?')[0]));
    } catch {
      return /\.pdf/i.test(url);
    }
  };

  const approvalPillClass = (value) =>
    value === 'Approved' ? 'good' : value === 'Denied' ? 'bad' : 'warn';

  // Compliance checklist progress for the summary card (medium + ftc-enhanced only)
  const getComplianceSummary = () => {
    const tier = dealershipInfo?.complianceTier;
    if (tier !== 'ftc-enhanced' && tier !== 'medium') return null;
    try {
      const autoResults = runAutoChecks(vehicles, prizes, orderDetails, dealershipInfo, tier);
      return getChecklistProgress(complianceChecklist, autoResults, tier);
    } catch {
      return null;
    }
  };

  const vehicleLabel = (v) => {
    const make = v.make === 'Other' ? (v.customMake || 'Other') : v.make;
    return [v.year, make, v.model].filter(Boolean).join(' ');
  };

  const filledPrizes = prizes.filter(p => p.prize);
  const filledVehicles = vehicles.filter(v => v.make || v.model);
  const latestProof = getLatestProof();
  const complianceSummary = getComplianceSummary();
  const hasComplianceCard = dealershipInfo?.complianceTier === 'ftc-enhanced' || dealershipInfo?.complianceTier === 'medium';
  const contractCount = (generatedContracts.length > 0 ? generatedContracts : orderDetails.insuranceContracts || []).length;
  const drop1 = orderDetails.drops?.[0];

  return (
    <div className="view-order-container">
      <OrderHeader 
  orderDetails={orderDetails}
  status={status}
  handleStatusChange={handleStatusChange}
  getStatusColor={getStatusColor}
  isEditing={isEditing}
  setIsEditing={setIsEditing}
  handleSave={handleSave}
  navigate={navigate}
  vehicles={vehicles}
  prizes={prizes}
  getArtDueDate={getArtDueDate}
  getOrderResponseRate={getOrderResponseRate}
  dealershipInfo={dealershipInfo}
  handleSendToJSDirect={handleSendToJSDirect}
  handleSendToClient={handleSendToClient}
  handleCopyOrder={handleCopyOrder}  
/>

      <DealershipCard 
        dealershipInfo={dealershipInfo}
        clientId={clientId}
        navigate={navigate}
      />

      {/* Compliance banner (medium = amber, ftc-enhanced = red) */}
      {(dealershipInfo?.complianceTier === 'ftc-enhanced' || dealershipInfo?.complianceTier === 'medium') && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: '10px',
          padding: '10px 16px',
          margin: '12px 0',
          background: dealershipInfo.complianceTier === 'ftc-enhanced' ? '#7f1d1d' : '#b45309',
          color: 'white',
          borderRadius: '8px',
          fontWeight: '700',
          fontSize: '14px',
          letterSpacing: '0.5px'
        }}>
          {dealershipInfo.complianceTier === 'ftc-enhanced' ? '🛡️ FTC-ENHANCED COMPLIANCE ORDER' : '⚠️ MEDIUM COMPLIANCE ORDER'}
          {dealershipInfo?.autoGroup && (
            <span style={{
              background: 'rgba(255,255,255,0.2)',
              padding: '2px 10px',
              borderRadius: '12px',
              fontSize: '12px',
              fontWeight: '600'
            }}>
              {dealershipInfo.autoGroup}
            </span>
          )}
          <span style={{ fontWeight: '400', fontSize: '12px', marginLeft: 'auto' }}>
            {dealershipInfo.complianceTier === 'ftc-enhanced'
              ? 'Enhanced disclaimer format & checklist required'
              : 'Compliance checklist required (standard disclaimer format)'}
          </span>
        </div>
      )}

      {/* Messages Section */}
      <div className="messages-section">
        <div className="message-column">
          <h3>Message Client About This Order</h3>
          <MessageForm 
            clientId={clientId} 
            recipientType="client"
            orderId={orderId}
            orderNumber={orderDetails.jobNumber}
            campaignName={orderDetails.campaignName}
          />
          <MessageLog 
            key={`client-order-${orderId}`}
            clientId={clientId} 
            recipientType="client"
            orderId={orderId}
            clearFlag={clearClientLog}
          />
          <div className="client-actions">
            <button
              style={{ marginBottom: 8 }}
              onClick={() => setClearClientLog(n => n + 1)}
            >
              Clear Order Chat
            </button>
          </div>
        </div>

        <div className="message-column">
          <h3>Internal Notes About This Order</h3>
          <MessageForm 
            clientId={clientId} 
            recipientType="internal"
            orderId={orderId}
            orderNumber={orderDetails.jobNumber}
            campaignName={orderDetails.campaignName}
          />
          <MessageLog 
            key={`internal-order-${orderId}`}
            clientId={clientId} 
            recipientType="internal"
            orderId={orderId}
            clearFlag={clearInternalLog}
          />
          <div className="client-actions">
            <button
              style={{ marginBottom: 8 }}
              onClick={() => setClearInternalLog(n => n + 1)}
            >
              Clear Internal Chat
            </button>
          </div>
        </div>
      </div>
        
      {/* ===== Magic Bento overview grid ===== */}
      <BentoGrid>
        <BentoCard
          label="Order Details"
          title={orderDetails.campaignName || `Job #${orderDetails.jobNumber || '-'}`}
          span={2}
          onClick={() => setActiveModal('details')}
        >
          <div className="bento-kv"><span>Job #</span><span>{orderDetails.jobNumber || '-'}</span></div>
          <div className="bento-kv"><span>Size</span><span>{orderDetails.size || '-'}</span></div>
          <div className="bento-kv"><span>Quantity</span><span>{orderDetails.quantity || '-'}</span></div>
          <div className="bento-kv"><span>Printer</span><span>{orderDetails.printer === 'Other' ? (orderDetails.printerOther || 'Other') : (orderDetails.printer || '-')}</span></div>
          <div className="bento-kv">
            <span>Drops</span>
            <span>{orderDetails.drops?.length || 0}{drop1?.startDate ? ` · starts ${formatDateToMMDDYYYY(drop1.startDate)}` : ''}</span>
          </div>
        </BentoCard>

        <BentoCard
          label="Vehicles"
          title={`${filledVehicles.length} vehicle${filledVehicles.length === 1 ? '' : 's'}`}
          onClick={() => setActiveModal('vehicles')}
        >
          {filledVehicles.length === 0 ? (
            <span className="bento-muted">No vehicles added yet</span>
          ) : (
            <>
              {filledVehicles.slice(0, 3).map((v, i) => (
                <div key={i} className="bento-list-item">{vehicleLabel(v) || `Vehicle ${i + 1}`}</div>
              ))}
              {filledVehicles.length > 3 && (
                <div className="bento-more">+{filledVehicles.length - 3} more</div>
              )}
            </>
          )}
        </BentoCard>

        <BentoCard
          label="Prizes"
          title={`${filledPrizes.length} of ${prizes.length} set`}
          onClick={() => setActiveModal('prizes')}
        >
          {filledPrizes.length === 0 ? (
            <span className="bento-muted">No prizes selected yet</span>
          ) : (
            <>
              <div className="bento-list-item">
                🏆 {filledPrizes[0].prize === 'Other' ? (filledPrizes[0].custom || 'Custom Prize') : filledPrizes[0].prize}
              </div>
              <div className="bento-kv"><span>Total odds</span><span>{totalOdds?.toLocaleString?.() || totalOdds}</span></div>
            </>
          )}
        </BentoCard>

        {/* Large design card - bottom left */}
        <BentoCard label="Design" span={2} tall onClick={() => setActiveModal('design')}>
          <div className="bento-pill-row">
            <span className={`bento-pill ${approvalPillClass(adminApproval)}`}>Admin: {adminApproval}</span>
            <span className={`bento-pill ${approvalPillClass(clientApproval)}`}>Client: {clientApproval}</span>
            {proofs.length > 0 && (
              <span className="bento-pill info">{proofs.length} proof{proofs.length === 1 ? '' : 's'}</span>
            )}
            {latestProof?.fileUrl && (
              <button
                type="button"
                className="bento-fullscreen-btn"
                onClick={(e) => { e.stopPropagation(); setDesignFullscreen(true); }}
                onKeyDown={(e) => e.stopPropagation()}
              >
                ⛶ View fullscreen
              </button>
            )}
          </div>
          <div className="bento-design-preview">
            {latestProof?.fileUrl ? (
              isPdfUrl(latestProof.fileUrl) ? (
                <iframe
                  src={`${latestProof.fileUrl}#toolbar=0&navpanes=0`}
                  title="Latest design proof"
                  style={{ pointerEvents: 'auto' }}
                />
              ) : (
                <img src={latestProof.fileUrl} alt="Latest design proof" />
              )
            ) : (
              <div className="bento-design-empty">No design uploaded yet - open to upload the first proof</div>
            )}
          </div>
        </BentoCard>

        <BentoCard
          label="Disclaimer"
          title={disclaimerEdited ? 'Manually edited' : 'Auto-generated'}
          span={2}
          onClick={() => setActiveModal('disclaimer')}
        >
          {disclaimerEdited && (
            <div className="bento-pill-row"><span className="bento-pill warn">⚠️ Manually Edited</span></div>
          )}
          <div className="bento-disclaimer-preview">
            {getDisplayDisclaimer() || 'Add vehicles and prizes to generate a disclaimer.'}
          </div>
        </BentoCard>

        {hasComplianceCard && (
          <BentoCard
            label="Compliance"
            title={dealershipInfo.complianceTier === 'ftc-enhanced' ? 'FTC-Enhanced' : 'Medium'}
            onClick={() => setActiveModal('compliance')}
          >
            {complianceSummary ? (
              complianceSummary.complete ? (
                <span className="bento-pill good">✓ Checklist complete</span>
              ) : (
                <>
                  <div className="bento-pill-row">
                    <span className="bento-pill bad">
                      {complianceSummary.outstanding.length} item{complianceSummary.outstanding.length === 1 ? '' : 's'} outstanding
                    </span>
                  </div>
                  <span className="bento-muted">Open to review the checklist</span>
                </>
              )
            ) : (
              <span className="bento-muted">Open to review the checklist</span>
            )}
          </BentoCard>
        )}

        <BentoCard
          label="Insurance"
          title={insuranceFile ? 'Policy linked' : 'SRMG insurance'}
          span={hasComplianceCard ? 1 : 2}
          onClick={() => setActiveModal('insurance')}
        >
          <div className="bento-pill-row">
            <span className={`bento-pill ${insuranceFile ? 'good' : 'info'}`}>
              {insuranceFile ? 'External policy linked' : 'No external policy'}
            </span>
          </div>
          <div className="bento-kv"><span>Contracts</span><span>{contractCount > 0 ? `${contractCount} generated` : 'Not generated'}</span></div>
        </BentoCard>

        <BentoCard
          label="Prizeboard"
          title={prizeboardFile ? 'Uploaded' : 'Not uploaded'}
          span={2}
          onClick={() => setActiveModal('prizeboard')}
        >
          <span className={`bento-pill ${prizeboardFile ? 'good' : 'warn'}`}>
            {prizeboardFile ? '✓ Prizeboard on file' : 'Prizeboard still needed'}
          </span>
        </BentoCard>
      </BentoGrid>

      {/* ===== Section modals (existing sections, unchanged) ===== */}
      <SectionModal isOpen={activeModal === 'details'} title="Order Details" onClose={() => setActiveModal(null)}>
        <div className="order-card">
          <OrderDetailsSection 
            orderDetails={orderDetails}
            setOrderDetails={setOrderDetails}
            isEditing={isEditing}
          />
        </div>
      </SectionModal>

      <SectionModal isOpen={activeModal === 'vehicles'} title="Vehicles" onClose={() => setActiveModal(null)}>
        <div className="order-card">
          <VehiclesSection 
            vehicles={vehicles}
            setVehicles={setVehicles}
            isEditing={isEditing}
            onImportRequest={handleOpenImportModal}
            complianceTier={dealershipInfo?.complianceTier}
          />
        </div>
      </SectionModal>

      <SectionModal isOpen={activeModal === 'prizes'} title="Prizes & Odds" onClose={() => setActiveModal(null)}>
        <div className="order-card">
          <PrizesSection 
            prizes={prizes}
            setPrizes={setPrizes}
            totalOdds={totalOdds}
            setTotalOdds={handleTotalOddsChange}  
            isEditing={isEditing}
            onPrizeChange={handlePrizeChange}
            onImportRequest={handleOpenImportModal}
          />
        </div>
      </SectionModal>

      <SectionModal isOpen={activeModal === 'disclaimer'} title="Disclaimer" onClose={() => setActiveModal(null)}>
      <div className="order-card">
          <div className="disclaimer-content">
            {/* Disclaimer Header with Controls */}
            <div style={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center', 
              marginBottom: '12px',
              flexWrap: 'wrap',
              gap: '8px'
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                {disclaimerEdited && (
                  <span style={{
                    background: '#fef3c7',
                    color: '#92400e',
                    padding: '2px 8px',
                    borderRadius: '12px',
                    fontSize: '12px',
                    fontWeight: '500'
                  }}>
                    ⚠️ Manually Edited
                  </span>
                )}
              </div>
              
              {/* Control Buttons */}
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {disclaimerEdited && !isEditingDisclaimer && (
                  <button
                    type="button"
                    onClick={handleDisclaimerRegenerate}
                    style={{
                      background: '#10b981',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                    title="Regenerate disclaimer from order details"
                  >
                    ↻ Regenerate from Order
                  </button>
                )}
                
                <button
                  type="button"
                  onClick={isEditingDisclaimer ? handleDisclaimerSave : handleDisclaimerEdit}
                  style={{
                    background: isEditingDisclaimer ? '#059669' : '#3b82f6',
                    color: 'white',
                    border: 'none',
                    padding: '6px 12px',
                    borderRadius: '6px',
                    fontSize: '12px',
                    cursor: 'pointer',
                    fontWeight: '500',
                  }}
                >
                  {isEditingDisclaimer ? '✓ Save Changes' : '✏️ Edit'}
                </button>
                
                {isEditingDisclaimer && (
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditingDisclaimer(false);
                      // Restore previous state without saving changes
                    }}
                    style={{
                      background: '#6b7280',
                      color: 'white',
                      border: 'none',
                      padding: '6px 12px',
                      borderRadius: '6px',
                      fontSize: '12px',
                      cursor: 'pointer',
                      fontWeight: '500',
                    }}
                  >
                    Cancel
                  </button>
                )}
              </div>
            </div>

            {/* Disclaimer Display/Edit Area */}
            {isEditingDisclaimer ? (
              <div>
                <textarea
                  value={manualDisclaimer}
                  onChange={(e) => setManualDisclaimer(e.target.value)}
                  style={{
                    width: '100%',
                    whiteSpace: 'pre-wrap',
                    minHeight: '400px',
                    padding: '12px',
                    border: '2px solid #3b82f6',
                    borderRadius: '6px',
                    fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                    fontSize: '12px',
                    lineHeight: '1.5',
                    resize: 'vertical',
                    background: '#fafafa'
                  }}
                  placeholder="Enter your custom disclaimer text here..."
                />
                <div style={{ 
                  marginTop: '8px', 
                  fontSize: '12px', 
                  color: '#6b7280',
                  fontStyle: 'italic' 
                }}>
                  Tip: You can edit this text manually. Use "Regenerate from Order" to restore auto-generated content.
                </div>
              </div>
            ) : (
              <div>
                <div style={{
                  whiteSpace: 'pre-wrap',
                  fontFamily: 'Monaco, Consolas, "Lucida Console", monospace',
                  fontSize: '12px',
                  lineHeight: '1',
                  background: '#f9fafb',
                  padding: '16px',
                  borderRadius: '6px',
                  border: '1px solid #e5e7eb',
                  margin: 0,
                  overflow: 'auto'
                }}>
                  {getDisplayDisclaimer()}
                </div>
                
                {(!vehicles.length || !prizes.length) && (
                  <div style={{
                    marginTop: '12px',
                    padding: '8px 12px',
                    background: '#fef3c7',
                    border: '1px solid #f59e0b',
                    borderRadius: '6px',
                    fontSize: '12px',
                    color: '#92400e'
                  }}>
                    ⚠️ Note: Add vehicles and prizes to generate a complete disclaimer.
                  </div>
                )}
              </div>
            )}
          </div>
      </div>
      </SectionModal>

      {/* Compliance checklist section (medium + ftc-enhanced tiers) */}
      {(dealershipInfo?.complianceTier === 'ftc-enhanced' || dealershipInfo?.complianceTier === 'medium') && (
        <SectionModal isOpen={activeModal === 'compliance'} title="Compliance Checklist" onClose={() => setActiveModal(null)}>
          <div className="order-card">
            <ComplianceSection
              vehicles={vehicles}
              prizes={prizes}
              orderDetails={orderDetails}
              dealershipInfo={dealershipInfo}
              checklist={complianceChecklist}
              setChecklist={setComplianceChecklist}
              tier={dealershipInfo.complianceTier}
            />
          </div>
        </SectionModal>
      )}

      {/* Fullscreen design proof viewer */}
      {designFullscreen && latestProof?.fileUrl && (
        <div
          className="bento-fullscreen-overlay"
          onMouseDown={(e) => { if (e.target === e.currentTarget) setDesignFullscreen(false); }}
        >
          <button
            type="button"
            className="bento-fullscreen-close"
            onClick={() => setDesignFullscreen(false)}
            aria-label="Close fullscreen"
          >
            ×
          </button>
          {isPdfUrl(latestProof.fileUrl) ? (
            <iframe
              className="bento-fullscreen-frame"
              src={`${latestProof.fileUrl}#navpanes=0`}
              title="Design proof fullscreen"
            />
          ) : (
            <img className="bento-fullscreen-img" src={latestProof.fileUrl} alt="Design proof fullscreen" />
          )}
        </div>
      )}

      <SectionModal isOpen={activeModal === 'design'} title="Design & Approvals" onClose={() => setActiveModal(null)}>
        <ApprovalSection 
          proofs={proofs}
          adminApproval={adminApproval}
          clientApproval={clientApproval}
          handleFileUpload={handleFileUpload}
          handleApprovalChange={handleApprovalChange}
          orderId={orderId}
          clientId={clientId}
          orderDetails={orderDetails}
          onSendForApproval={handleSendForApproval}
          dealershipInfo={dealershipInfo}  
        />
      </SectionModal>

      {/* Enhanced Insurance Section */}
      <SectionModal isOpen={activeModal === 'insurance'} title="Insurance & Winning Numbers" onClose={() => setActiveModal(null)}>
      <div className="order-card">
    <div className="insurance-section">
      {/* Insurance Status Header */}
      <div className="insurance-status-card">
        <div className="insurance-header">
          <h3>Insurance Policy</h3>
          <div className={`insurance-status-badge ${insuranceFile ? 'linked' : 'not-uploaded'}`}>
            {insuranceFile ? 'Policy Linked' : 'No Policy'}
          </div>
        </div>
      </div>

      {/* Contract Generation Section */}
      <div className="contract-generation-section" style={{ marginBottom: '24px', padding: '20px', background: '#f8fafc', borderRadius: '8px' }}>
        <h4 style={{ margin: '0 0 16px 0', fontSize: '16px', fontWeight: '600', color: '#1f2937' }}>
          Generate SRMG Insurance Contracts
        </h4>
        
        {generatedContracts.length > 0 ? (
          <div>
            <p style={{ color: '#059669', marginBottom: '16px', fontSize: '14px', fontWeight: '500' }}>
              Contracts generated on {new Date(generatedContracts[0].createdAt).toLocaleDateString()}
            </p>
            <div className="generated-contracts-list">
              {generatedContracts.map(contract => (
                <div key={contract.id} style={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  padding: '12px',
                  background: 'white',
                  border: '1px solid #e5e7eb',
                  borderRadius: '6px',
                  marginBottom: '8px'
                }}>
                  <span style={{ fontWeight: '500', color: '#374151' }}>
                    {contract.name.replace(/_/g, ' ')}
                  </span>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      onClick={() => window.open(contract.url, '_blank', 'noopener,noreferrer')}
                      style={{
                        padding: '6px 12px',
                        background: '#2563eb',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      View PDF
                    </button>
                    <button
                      onClick={() => handleDownload(contract.url, `${contract.name}.pdf`)}
                      style={{
                        padding: '6px 12px',
                        background: '#059669',
                        color: 'white',
                        textDecoration: 'none',
                        borderRadius: '4px',
                        fontSize: '12px',
                        fontWeight: '500'
                      }}
                    >
                      Download
                    </button>
                  </div>
                </div>
              ))}
            </div>
            
            <button
              onClick={generateInsuranceContract}
              disabled={isGeneratingContract || !isEditing}
              style={{
                marginTop: '12px',
                padding: '8px 16px',
                background: '#f59e0b',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isGeneratingContract || !isEditing ? 'not-allowed' : 'pointer',
                opacity: isGeneratingContract || !isEditing ? 0.6 : 1
              }}
            >
              {isGeneratingContract ? 'Regenerating...' : 'Regenerate Contracts'}
            </button>
          </div>
        ) : (
          <div>
            <p style={{ color: '#6b7280', marginBottom: '16px', fontSize: '14px' }}>
              Generate insurance contracts based on this order's details including prizes, dates, and dealership information.
            </p>
            <button
              onClick={generateInsuranceContract}
              disabled={isGeneratingContract || !isEditing}
              style={{
                padding: '12px 24px',
                background: '#059669',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: isGeneratingContract || !isEditing ? 'not-allowed' : 'pointer',
                opacity: isGeneratingContract || !isEditing ? 0.6 : 1
              }}
            >
              {isGeneratingContract ? 'Generating Contracts...' : 'Generate Insurance Contracts'}
            </button>
            
            <div style={{ marginTop: '12px', fontSize: '12px', color: '#6b7280' }}>
              This will generate:
              <ul style={{ margin: '8px 0', paddingLeft: '20px' }}>
                <li>Official Rules document</li>
                <li>Promotion Execution Checklist</li>
                <li>Winner's Affidavit of Eligibility form</li>
              </ul>
              All documents will include the SRMG logo.
            </div>
            
          </div>
        )}
      </div>

        <WinningNumbersGenerator
    orderId={orderId}
    prizes={prizes}
     clientId={clientId}
 orderNumber={orderDetails.jobNumber}
 campaignName={orderDetails.campaignName}
    defaultCounts={{ 4: 15 }}
    isEditing={isEditing} 
  />
</div>

      <div className="order-card">
      {/* Existing Insurance Upload Section */}
      <div className="insurance-upload">
        <h4>External Insurance Document (Optional)</h4>
        
        {insuranceFile ? (
          <div className="insurance-file-display">
            <div className="insurance-file-info">
              <FileText className="w-8 h-8 text-green-600" />
              <div className="insurance-file-details">
                <span className="insurance-file-name">Insurance Policy</span>
                <span className="insurance-file-status">Ready for use</span>
              </div>
            </div>
            <div className="insurance-file-actions">
              <a
                href={insuranceFile}
                target="_blank"
                rel="noopener noreferrer"
                className="insurance-view-button"
              >
                <FileText className="w-4 h-4" />
                View Policy
              </a>
              {isEditing && (
                <button
                  type="button"
                  onClick={() => insuranceInputRef.current?.click()}
                  className="insurance-replace-button"
                >
                  Replace
                </button>
              )}
            </div>
          </div>
        ) : (
          <div className="insurance-upload-area">
            <div className="insurance-upload-content">
              <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
              <h5>Upload External Insurance Policy</h5>
              <p>Only needed if using a third-party insurance provider</p>
              <button
                type="button"
                disabled={!isEditing}
                onClick={() => insuranceInputRef.current?.click()}
                className="insurance-upload-button"
              >
                <FileText className="w-4 h-4" />
                Choose File
              </button>
            </div>
          </div>
        )}

        {/* Hidden file input */}
        <input
          type="file"
          ref={insuranceInputRef}
          disabled={!isEditing}
          hidden
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) handleFileUpload(file, 'insurance');
          }}
        />
      </div>
    </div>
</div>
      </SectionModal>

      {/* Enhanced Prizeboard Section */}
      <SectionModal isOpen={activeModal === 'prizeboard'} title="Prizeboard" onClose={() => setActiveModal(null)}>
        <PrizeboardSection 
          orderDetails={orderDetails}
          setOrderDetails={setOrderDetails}
          prizeboardFile={prizeboardFile}
          isEditing={isEditing}
          handleFileUpload={handleFileUpload}
          dealershipInfo={dealershipInfo}
        />
      </SectionModal>

      <CopyOrderModal
        isOpen={showCopyModal}
        onClose={() => setShowCopyModal(false)}
        orderDetails={orderDetails}
        vehicles={vehicles}
        prizes={prizes}
        proofs={proofs}
        insuranceFile={insuranceFile}
        insurancePath={insurancePath}
        prizeboardFile={prizeboardFile}
        onCopy={handleCopyOrderSubmit}
      />

      <ImportFromOrderModal
        isOpen={importModal.open}
        onClose={() => setImportModal({ open: false, mode: null })}
        mode={importModal.mode}
        currentOrderId={orderId}
        onImport={handleImportFromOrder}
      />
    </div>
    
  );
}

export default ViewOrderPage;